/**
 * Provider-Konfiguration für die BpWeb-App.
 * Nutzt provideLuzulaApp() um alle Library-Tokens in einem Aufruf zu konfigurieren.
 */

import { Provider, Type, inject } from '@angular/core';
import Keycloak from 'keycloak-js';
import {
	provideLuzulaApp,
	SyncProgressComponent,
	DownloadProgressComponent,
	OfflineDetailComponent,
	WdFilterComponent,
	ConfirmService,
	WMS_AUTH_TOKEN_PROVIDER,
	POINT_LABEL_PROVIDER,
	PROJECT_BACKUP_HANDLER,
	PROJECT_ROW_ACTIONS,
	ZEICHNUNG_STORE,
	BEREICH_STORE,
	GEOM_STORE,
	GEOM_LAYER_DEFAULTS,
	IValidationService,
	DB_CONNECTION_CONFIGS,
	OFFLINE_DOCUMENT_GROUPS,
	VERBINDUNG_PRUEF_URL,
	GROUP_ASSIGN_ENABLED,
	PROJECT_DOC_GROUP_STAMPER,
	provideFehlerMelden,
} from '@luzula/luzula-lib';
import { NgbModal } from '@ng-bootstrap/ng-bootstrap';
import { ControlEvaluationModalComponent } from '../bohrpunkte/detail/control-evaluation/control-evaluation-modal.component';
import { ZeichnungenService } from '../zeichnungen.service';
import { BereicheService } from '../bereiche.service';
import { GeomFeaturesService } from '../geom-features.service';
import { LayoutsService } from '../layouts.service';
import { buildBpwebGeomLayers } from '../map/geom-default-layers';
import { ProjectBackupHandlerService } from '../import/project-backup-handler.service';
import { BohrpunktHelpExtensionHandler } from '../bohrpunkte/help/help-extension.handler';
import { AuthService } from '../auth.service';
import { ProjectService } from '../project.service';
import { DataService } from '../data.service';
import { FilterService } from '../filter.service';
import { NavbarService } from '../navbar.service';
import { KeycloakAdminService } from '../keycloak-admin.service';
import { Values } from '../bohrpunkte/filter/application.filter';
import { EvaluationService } from '../bohrpunkte/evaluation.service';
import { ValidationService } from '../bohrpunkte/validation.service';
import { OptionService } from '../bohrpunkte/option.service';
import { HelpService } from '../bohrpunkte/help/help.service';
import { ListDataProvider } from '../bohrpunkte/list-data-provider';
import { BohrpunktDocumentHandler } from '../bohrpunkte/bohrpunkt-document.handler';
import { buildDbConnectionConfigs } from './db-connection.config';
import { bpwebProjectRolesConfig } from './project-roles.config';
import { BohrpunktWmsFieldMappingHandler, BohrpunktGpkgAutoStyleHandler, BohrpunktMapMarkerHandler, BohrpunktProjectAssignmentHandler, BohrpunktDataLifecycleHandler } from '../bohrpunkte/handlers';
import { BohrpunktNavigationHandler } from '../map/bohrpunkt-navigation.handler';
import wmsJson from '../../assets/wms/wms.json';
import pkg from '../../../package.json';
import { environment } from '../../environments/environment';
import { ALL_MIGRATIONS } from '../migrations/all-migrations';
import { CURRENT_VERSION } from '../data-model';
import { makeSeaDocumentGroups } from '../sea-documents';
import { ListenService } from '../listenverwaltung/listen.service';
import { BOHRPUNKT_BATCH_EDIT_FIELDS } from '../bohrpunkte/batch-edit/bohrpunkt-batch-edit-fields';
import { VERFAHREN_SECTION } from '../bohrpunkte/verfahren/verfahren-section.component';
import { stempleProjektBestand } from './projekt-bestand-stempeln';

/**
 * Alle Library-Provider für BpWeb.
 * Ersetzt die früheren APP_PROVIDERS, CONTROLS_PROVIDERS und die Handler-Provider aus app.module.ts.
 */
export const APP_PROVIDERS: Provider[] = [
	...provideLuzulaApp({
	// Zentrale dev-/prod-DB-Namensbildung (LuzulaDbNaming/DB_ENV_PREFIX):
	// dev-Build praefixiert alle DB-Namen mit 'dev-'.
	isProduction: environment.production,
	auth: { provider: AuthService, context: AuthService },
	data: {
		provider: DataService,
		documentHandler: BohrpunktDocumentHandler,
		dbConfigs: [], // Platzhalter — der reale Provider kommt via useFactory(buildDbConnectionConfigs) (Gateway, braucht DI/Keycloak) und ueberschreibt diesen
		// Melden nur: Zwei Fassungen einer Zeichnung oder einer Editor-Flaeche
		// wurden nirgends angezeigt (die Geometrie-DB wurde bis zum 29.08.2026
		// gar nicht geprueft). Aufloesen kann bpweb sie nicht — sagen muss es sie.
		konfliktQuellen: [ZeichnungenService, GeomFeaturesService],
	},
	project: { provider: ProjectService },
	filter: {
		provider: FilterService,
		component: WdFilterComponent,
		values: Values,
	},
	navbar: NavbarService,
	services: {
		evaluation: EvaluationService,
		// Cast umgeht eine rxjs-Typvarianzfalle: in der publizierten luzula-lib-Variante
		// liest tsc commentsChanged.observers als Observer<string>[], waehrend
		// IValidationService Observer<string | null>[] erwartet. Im Workspace-Dev-Modus
		// stimmen die Typen ueberein (gleiche Source); Cast schadet dort nicht.
		validation: ValidationService as unknown as Type<IValidationService>,
		option: OptionService,
		help: HelpService,
		helpExtension: BohrpunktHelpExtensionHandler,
		confirm: ConfirmService,
		keycloakAdmin: KeycloakAdminService,
		listData: ListDataProvider,
	},
	components: {
		syncProgress: SyncProgressComponent,
		downloadProgress: DownloadProgressComponent,
		offlineDetail: OfflineDetailComponent,
	},
	handlers: {
		mapMarker: BohrpunktMapMarkerHandler,
		mapNavigation: BohrpunktNavigationHandler,
		wmsFieldMapping: BohrpunktWmsFieldMappingHandler,
		gpkgAutoStyle: BohrpunktGpkgAutoStyleHandler,
		projectAssignment: BohrpunktProjectAssignmentHandler,
		dataLifecycle: BohrpunktDataLifecycleHandler,
	},
	config: {
		sensitiveFields: ['Kartierer', 'Aufnahmedatum'],
		projectRoles: bpwebProjectRolesConfig,
		// Gateway-WMS (relative `/wms`-URLs) auf den konfigurierten API-Host
			// umbiegen; externe Dienste (brandenburg-forst.de, geodaten-mv.de,
			// absolute URLs) bleiben unangetastet.
			wms: wmsJson.qgsWMSConnections.wms.map((c: any) => ({
				...c,
				url: typeof c.url === 'string' && c.url.startsWith('/') ? environment.apiBaseUrl + c.url : c.url,
			})),
		appVersion: pkg.version,
		appId: 'bpweb',
		// Gateway-Segment dieser Build-Variante (boden / bpwebmv / bpweblfmv) —
		// NICHT dasselbe wie appId: Die bpweb-Bauten teilen sich eine appId
		// (Backup-Vertraeglichkeit), haben aber je ein eigenes Segment. Der
		// Systemstatus zeigt dadurch nur noch, was DIESE App angeht.
		appSegment: environment.dbAppName,
		cleanupVersions: ['1.2.0', '1.5.7'],
		versionStorageKey: 'bpweb_last_version',
		currentDataVersion: CURRENT_VERSION,
		builtInMigrations: ALL_MIGRATIONS,
		routeCacheTargets: ['handbuch', 'listenverwaltung'],
		// offlineDocumentGroups NICHT hier (useValue) setzen — die SEA-Original-
		// Scans werden dynamisch aus dem Bodensteckbrief-Katalog abgeleitet und
		// brauchen den ListenService. Deshalb weiter unten als useFactory-Provider.
		// An die konfigurierte luzula-api gebunden (prod: api.luzula.de, dev-new:
		// dev-api.luzula.de) statt fest verdrahtet — sonst bliebe dev fuer
		// Geodaten/DGM/Health auf dem prod-Host. Health folgt ueber den
		// HEALTH_API_URL-Fallback auf geodataApiUrl automatisch mit.
		dgmApiUrl: environment.apiBaseUrl,
		geodataApiUrl: environment.apiBaseUrl,
		// Projekt-Berechtigungs-Flags (anlegen/loeschen/importieren/aendern/zuordnen/
		// Bereiche) sind in bpweb abgeschafft: Rollen + Workflow-Status regeln alles,
		// die projektweiten Flags stifteten nur Verwirrung (sie konnten nur sperren,
		// betrafen ALLE inkl. Owner). UI ausgeblendet; die can*-Gates ignorieren die
		// (ggf. an Altprojekten noch vorhandenen) Flags. Default-Schreibwert bleibt
		// all-true (harmlos, da ungelesen).
		permissionsVisible: false,
	},
	batchEditFields: BOHRPUNKT_BATCH_EDIT_FIELDS,
	// Wahl des Kartierverfahrens (SEA 95 oder KA 6) beim ANLEGEN eines Projekts.
	// Immer angemeldet; solange die KA-Aufnahme im Bau ist, sehen den Abschnitt
	// nur Entwickler (`sichtbar` am Abschnitt, Regel in `ka-schalter.ts`).
	projectNewSections: [VERFAHREN_SECTION],
	userManagement: {
		apiBaseUrl: environment.apiBaseUrl,
		// DB-Admin-Couch-Segment (/db/admin/couch/<appClientId>) = boden (KNOWN_APP).
		// Der Keycloak-Client fuer Rollen/Benutzerverwaltung bleibt separat 'bpweb'.
		appClientId: 'boden',
	},
	profile: {
		apiBaseUrl: environment.apiBaseUrl,
	},
	}),
	// DB-Verbindungs-Provider: ueberschreibt den von provideLuzulaApp() gesetzten
	// (leeren) useValue-Provider durch eine Factory, die die Gateway-Variante
	// liefert (Bearer-Token, keine Credentials im Bundle). Muss NACH
	// ...provideLuzulaApp(...) stehen, damit der Factory-Provider gewinnt.
	{
		provide: DB_CONNECTION_CONFIGS,
		useFactory: buildDbConnectionConfigs,
	},
	// Verbindungspruefung gegen die EIGENE Schnittstelle statt gegen einen fremden
	// Server. Vorher fragte die App dafuer alle 10 Sekunden client3.google.com —
	// das sagt nichts darueber, ob unsere Daten erreichbar sind, und jedes Geraet
	// meldete sich damit regelmaessig bei einem Dritten (am Tablet gemessen
	// 03.09.2026). Im Offline-Modus schweigt die Pruefung jetzt ganz.
	//
	// `/health/live` und NICHT `/health`: Das grosse `/health` klopft bei jedem
	// Aufruf Keycloak, mehrere CouchDBs und das DB-Gateway ab — alle 10
	// Sekunden, von jedem Geraet. `/health/live` sagt nur „ich bin da" (74 Byte,
	// keine Abhaengigkeiten), und gefragt wird hier ohnehin nur, ob ueberhaupt
	// etwas zurueckkommt (siehe connection.service.ts der Bibliothek).
	{
		provide: VERBINDUNG_PRUEF_URL,
		useValue: `${environment.apiBaseUrl}/health/live`,
	},
	// Offline-Dokumentgruppen: statische SEA-PDFs + dynamische SEA-Original-Scans.
	// Als Factory (statt useValue in provideLuzulaApp), weil die Original-URLs erst
	// beim Download aus dem laufzeit-geladenen Bodensteckbrief-Katalog (ListenService)
	// abgeleitet werden. Muss NACH ...provideLuzulaApp(...) stehen.
	{
		provide: OFFLINE_DOCUMENT_GROUPS,
		useFactory: (listen: ListenService) =>
			makeSeaDocumentGroups(() => listen.getList('Bodensteckbriefe') as Promise<any>),
		deps: [ListenService],
	},
	// Bearer-Token-Provider fuer WMS-Layer mit auth: 'bearer' (z.B. DenkmalGIS
	// ueber den Gateway /wms/...). Liest pro Tile-Request den aktuellen
	// Keycloak-Token, sodass Token-Rotation automatisch greift.
	{
		provide: WMS_AUTH_TOKEN_PROVIDER,
		useFactory: () => {
			const kc = inject(Keycloak);
			return () => kc.token;
		}
	},
	// Punkt-Label-Provider: Workflow-Editor/Bulk-Report sollen die Punktnummer
	// statt der Doc-ID anzeigen, wenn vorhanden.
	{
		provide: POINT_LABEL_PROVIDER,
		useValue: (doc: any) => {
			const nr = doc?.header?.nummer;
			return nr ? `Nr. ${nr}` : doc?._id;
		}
	},
	// Projekt-Backup/Restore (User-Wunsch 2026-06-06): bpweb-spezifischer
	// Handler kapselt Punkt-Sammlung + ImportWizard-Restore-Mode.
	{ provide: PROJECT_BACKUP_HANDLER, useExisting: ProjectBackupHandlerService },
	// Zeichnungen-Store (dedizierte bpweb-zeichnungen-DB); DrawingService nutzt ihn optional.
	{ provide: ZEICHNUNG_STORE, useExisting: ZeichnungenService },
	// Bereiche-Store (dedizierte bpweb-bereiche-DB, Doc je Bereich mit stabiler ID);
	// Konsumenten folgen in P2/P3 (PLAN-bereiche-db.md), bis dahin Fallback Projekt-Doc.
	{ provide: BEREICH_STORE, useExisting: BereicheService },
	// Geom-Store (dedizierte bpweb-geom-DB, Doc je Flaeche — Standort/Humusform);
	// Konsument (Karten-Editor) folgt in G2 (PLAN-flaechen-linien.md).
	{ provide: GEOM_STORE, useExisting: GeomFeaturesService },
	// Gruppen-Zuordnung im UI aktiv (feature/user-groups): Pflicht-Dialog beim
	// Oeffnen ungruppierter Projekte + editierbares Gruppen-Feld im Projekt-Dialog.
	// bpweb wird serverseitig gruppen-erzwungen (GROUP_ENFORCED_APPS), sobald die
	// Migration je Betrieb durch ist. Top-Level-Gruppen sind strikt getrennt.
	{ provide: GROUP_ASSIGN_ENABLED, useValue: true },
	// Retroaktives Nachstempeln ALLER Projekt-Dokumente beim Gruppen-Zuordnen:
	// Punkte (DataService, geteilte Remote-DB, Projekt-Zuordnung via header.projects
	// oder workflow.projectId, per Differenz — ein Punkt kann zu mehreren Projekten
	// gehoeren) + die vier dedizierten, projekt-scoped Stores (Geom/Zeichnung/Bereich/
	// Layout, gesetzt: ihre Dokumente gehoeren genau einem Projekt).
	// Neue Saves erben die groups ohnehin generisch (BaseDataService bzw. die Stores).
	// Scheitert ein Teil, wirft `stempleProjektBestand` nach dem letzten — die
	// Bibliothek meldet es (bis 30.09.2026 verschluckte ein blankes allSettled das).
	{
		provide: PROJECT_DOC_GROUP_STAMPER,
		useFactory: () => {
			const data = inject(DataService);
			const geom = inject(GeomFeaturesService);
			const zeichnung = inject(ZeichnungenService);
			const bereich = inject(BereicheService);
			const layout = inject(LayoutsService);
			return {
				stampAll: (projectId: string, groups: string[], vorher?: string[]) =>
					stempleProjektBestand({
						Punkte: (p, g, v) => data.stampeGroups(p, g, v),
						Geometrien: (p, g) => geom.stampeGroups(p, g),
						Zeichnungen: (p, g) => zeichnung.stampeGroups(p, g),
						Bereiche: (p, g) => bereich.stampeGroups(p, g),
						Layouts: (p, g) => layout.stampeGroups(p, g),
					}, projectId, groups, vorher),
			};
		},
	},
	// Vorgabe-Layer fuer den (Lib-)Flaechen-Editor. sto/humus sind bpweb-fachlich
	// und werden hier hereingereicht — die Lib selbst kennt keine Themen.
	// Fachwert-Kataloge kommen live aus den bpweb-Listen (DataService) + der
	// Humus-Regel (BohrpunktGpkgAutoStyleHandler) -> Factory statt statischem Wert.
	{ provide: GEOM_LAYER_DEFAULTS, useFactory: buildBpwebGeomLayers, deps: [DataService, BohrpunktGpkgAutoStyleHandler] },
	// Pro-Zeilen-Aktion in der Projektauswahl (User-Wunsch 2026-06-19):
	// „Kontroll-Auswertung" je Projekt — öffnet Punktstati + Kommentar-Statistik
	// + Handlungsbedarf, ohne das Projekt vorher öffnen zu müssen. Das Modal lädt
	// seine Daten selbst (ein Index-Request) über den DataService.
	{
		provide: PROJECT_ROW_ACTIONS,
		useFactory: () => {
			const modal = inject(NgbModal);
			return [{
				label: 'Kontroll-Auswertung',
				icon: 'bi-bar-chart-line',
				action: (project: { _id: string; name?: string }) => {
					const ref = modal.open(ControlEvaluationModalComponent, { size: 'lg', scrollable: true });
					ref.componentInstance.projectId = project._id;
					ref.componentInstance.projectName = project.name ?? '';
				},
			}];
		},
	},
	// Fehler an das GitLab-Fehlerprotokoll (Nutzer-Auftrag 25.09.2026: „alles
	// haette ich gern"). Meldet, was der Kaefer zaehlt — und nur das. Lokal aus;
	// die letzte Entscheidung faellt serverseitig im Tunnel der luzula-api je
	// <app>@<host> (routes/error-tracking.config.js); ohne DSN dort kommt nichts
	// an. Zum Testen der Kette lokal: localStorage.wd_error_tracking = 'on'.
	...provideFehlerMelden({
		tunnelBasis: environment.apiBaseUrl,
		app: environment.dbAppName,
		version: pkg.version,
	}),
];
